/**
 * PendingDecisionMarks — RRA v2.2 part B (WEL-180): the builder's "Decision pending" tick per animal.
 *
 * Rendered inside AnimalsSection only while the review summary reports an open ANIMAL accommodation
 * request. Staff tick the animal(s) the request is about; each ticked animal shows
 * `If not approved: rent $__ / month, deposit $__` (defaulting to this draft's per-animal rates) and
 * writes `LeaseFormState.animalsPendingDecision`. A marked animal is $0 now; the lease prints one
 * neutral pending-decision clause. Unticked animals are charged normally. Until something is marked an
 * advisory says to mark the animal(s) — it never blocks.
 *
 *   cd apps/web && npx tsx --test src/components/PendingDecisionMarks.test.ts
 *
 * Internal staff copy: nothing here reaches the lease. Every input is labelled; no id is rendered.
 */

import { MoneyInput } from '@/components/MoneyInput';
import type { LeaseFormState } from '@/lib/leaseConfig';
import {
  DECISION_PENDING_LABEL,
  PENDING_HELP,
  pendingAdvisory,
  pendingEntryFor,
  pendingSummaryLine,
  setPendingAmount,
  togglePending,
  type MarkableAnimal,
  type PendingDecisionEntry,
} from '@/lib/rraPendingDecision';

type Rates = Pick<LeaseFormState, 'animalRentPerAnimal' | 'animalDepositPerAnimal'>;

export function PendingDecisionMarks(props: {
  /** The animals the builder can mark — the review summary's merged roster, else the draft's pets (lib `markableAnimalsFor`). */
  animals: ReadonlyArray<MarkableAnimal>;
  /** The draft's current marks. */
  marked: ReadonlyArray<PendingDecisionEntry> | undefined;
  /** The draft's per-animal rates — a fresh tick starts from them. */
  rates: Rates;
  /** The review summary says an animal request is open (the block renders only then). */
  animalRequestOpen: boolean;
  onChange: (next: PendingDecisionEntry[] | undefined) => void;
}): React.JSX.Element | null {
  const { animals, marked, rates, animalRequestOpen, onChange } = props;
  if (!animalRequestOpen || animals.length === 0) return null;
  const advisory = pendingAdvisory(animalRequestOpen, marked);
  const summary = pendingSummaryLine(marked);

  return (
    <div className="field" data-section="rra-pending-decision">
      <label>Accommodation request — {DECISION_PENDING_LABEL.toLowerCase()} (internal — not lease text)</label>
      <span className="helper">{PENDING_HELP}</span>
      {advisory && (
        <div className="alert" data-tone="warning" role="status" style={{ marginTop: 'var(--space-2)' }}>
          <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>{advisory}</div>
        </div>
      )}
      <div className="stack-sm" style={{ marginTop: 'var(--space-2)' }}>
        {animals.map((animal, i) => {
          const entry = pendingEntryFor(animal, marked);
          const markable = !animal.blocked;
          const base = `pending-${i}`;
          return (
            <div key={`${animal.key || animal.label}|${i}`} className="stack-sm">
              <label className="choice" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  className="check"
                  checked={entry !== undefined}
                  disabled={!markable}
                  onChange={(e) => onChange(togglePending(marked, animal, e.target.checked, rates))}
                />
                <span>
                  <strong>{animal.label}</strong> — {DECISION_PENDING_LABEL}
                </span>
              </label>
              {animal.blocked === 'unnamed' && (
                <span className="helper">Give this animal a name in AppFolio or the animal intake first.</span>
              )}
              {animal.blocked === 'exempt' && (
                <span className="helper">Already at no charge — nothing is pending for this animal.</span>
              )}
              {entry && (
                <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'var(--space-6)' }}>
                  <span className="helper">If not approved:</span>
                  <label htmlFor={`${base}-rent`} className="helper">rent</label>
                  <MoneyInput
                    id={`${base}-rent`}
                    valueCents={entry.ifDeniedRentCents}
                    onChangeCents={(c) => onChange(setPendingAmount(marked, entry.key, 'ifDeniedRentCents', c))}
                    ariaLabel={`If not approved, monthly rent for ${animal.name}`}
                  />
                  <span className="helper">/ month, </span>
                  <label htmlFor={`${base}-deposit`} className="helper">deposit</label>
                  <MoneyInput
                    id={`${base}-deposit`}
                    valueCents={entry.ifDeniedDepositCents}
                    onChangeCents={(c) => onChange(setPendingAmount(marked, entry.key, 'ifDeniedDepositCents', c))}
                    ariaLabel={`If not approved, deposit for ${animal.name}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {summary && (
        <span className="helper" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
          {summary}
        </span>
      )}
    </div>
  );
}
